import { ApiStudentRequest, request } from '../utils'
import { useAuthMutation } from './auth'
import { CreateLanguageProficiencyRequestAttributes } from '@backend/resources/student-document/language-proficiency'
import {
  CreateLanguageProficiencyRequest,
  CreateLanguageProficiencyResponse,
} from '@backend/students-api/routes/documents/language-proficiencies/POST/types'
import { useS3Upload } from './useS3Upload'
import { useQueryClient } from '@tanstack/react-query'

type UploadLanguageProficiencyParams = CreateLanguageProficiencyRequest['data'] & {
  file?: File
}

async function CreateLanguageProficiency(data: CreateLanguageProficiencyRequestAttributes) {
  const response = await request<CreateLanguageProficiencyResponse>(
    new ApiStudentRequest(`/documents/language-proficiencies`, {
      method: 'POST',
      body: {
        data: data,
      },
    }),
    {
      isExpectedResponse: (res): res is CreateLanguageProficiencyResponse => res as boolean,
    },
  )

  return response
}

export function useCreateLanguageProficiency() {
  const { s3UploadAsync } = useS3Upload()
  const queryClient = useQueryClient()

  const { isPending, mutate, mutateAsync } = useAuthMutation(
    async (params: { data: UploadLanguageProficiencyParams }) => {
      const { file, ...data } = params.data
      const response = await CreateLanguageProficiency(data)
      if (file) {
        const languageProficiencyUpload =
          response.data.meta.files[params.data.attributes.fileName].upload

        await s3UploadAsync({
          file,
          url: languageProficiencyUpload?.url!,
          fields: languageProficiencyUpload?.fields!,
        })
      }
      return response
    },
    {
      onSuccess: data => {
        // Invalidate language proficiency queries to refetch the data
        queryClient.invalidateQueries({
          queryKey: ['language-proficiency', data.data.id],
        })
      },
    },
  )

  return {
    isCreatingLanguageProficiency: isPending,
    createLanguageProficiency: mutate,
    createLanguageProficiencyAsync: mutateAsync,
  }
}
